/**
 * frontend/src/components/valuation/ValuationShell.tsx
 *
 * VAL-SHELL-1B — Valuation's own shell: a top bar, no left rail, and (on
 * engagement routes) a tab strip. Swapped in at router/valuation.tsx:82 in
 * place of the shared `AppShell`, which stays untouched (it is also used by
 * QoE and Attest — see that file's header comment).
 *
 * State-badge data-model gap (VAL-SHELL-1B R3.2), narrowed by
 * VAL-VERSION-DIFF-1: the badge now reads the saved engagement.
 * `engagement.status === 'signed_off'` drives Signed directly, and its
 * version comes from `fetchSnapshotHistory()`'s latest row rather than a
 * hardcoded 1 (a fetch error keeps the fallback of 1 — the badge degrades,
 * it does not throw). Draft/Computed derive from `engagement.status`
 * directly (VAL-INDUSTRY-WIRING-1 R6C — the signed engine's cached run has
 * no `status` field; `/run` only ever echoed the engagement row's own
 * status anyway, so this is the same value read one hop shorter). "Revising"
 * is wired but structurally unreachable today — there is no server state
 * that ever produces it.
 * Likewise, the cache carries no engagement display name — VAL-
 * ACTOR-JOURNEY-1-SIGNER R8 fetches it directly (`getValuationEngagement`)
 * rather than extending either cache, since the header is the only
 * consumer.
 */
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { getValuationEngagement } from '@/api/valuation'
import { _ValuationNotFound } from '@/pages/valuation/_ValuationNotFound'
import { fetchSnapshot, fetchSnapshotHistory } from '@/api/valuationB3'
import { fetchFinancials } from '@/api/valuationFinancials'
import { startHandoff } from '@/api/platformHandoff'
import { fetchPlatformAdminUrl } from '@/api/valuationPlatformAdmin'
import { runCache } from '@/pages/valuation/_shared'
import { nextStep } from '@/pages/valuation/_nextStep'
import { railNextRoute } from '@/pages/valuation/_railNext'
import type { ValuationEngagement } from '@/types/valuation'

type ShellState = 'draft' | 'computed' | 'signed' | 'revising'

function deriveState(status: string | undefined): ShellState {
  if (status === 'revising') return 'revising' // unreachable today — see file header
  if (status === 'signed_off') return 'signed'
  if (status === 'calculated' || status === 'in_cpa_review') return 'computed'
  return 'draft'
}

export function badgeLabel(state: ShellState, version: number): string {
  switch (state) {
    case 'signed': return `Signed v${version}`
    case 'revising': return `Revising → v${version + 1}`
    case 'computed': return 'Computed'
    default: return 'Draft'
  }
}

type TabKey = 'overview' | 'company' | 'financials' | 'adjust' | 'intake' | 'methodology' | 'provenance' | 'signoff'

interface TabDef {
  key: TabKey
  label: (state: ShellState, version: number) => string
  path: (id: string) => string
  dot: (state: ShellState) => 'done' | 'blocked' | 'none'
  dimUntilYearRead?: boolean // dimmed (still a link) while the engagement has no fiscal year read from an upload
}

// VAL-ACTOR-JOURNEY-1-SIGNER R3: real destinations only, in the rail's
// order. Deleted: `Profile` (duplicated `/valuation`, the dashboard link
// above), `Value drivers` (the second of two tabs pointing at `/output` —
// `provenance` below is the one whose label ("Value provenance") matches
// what that screen actually is), the `Document` label (no backing screen
// at all), and — slice A only — `overview#documents`, since that hash isn't
// a real screen either (removed properly below instead of left as a fake
// route). VAL-DASHBOARD-NEXT-1: the inert `History` item is gone too; History
// is the top-level link under VALUATION (/valuation/history).
const TABS: TabDef[] = [
  { key: 'overview', label: () => 'Overview', path: id => `/valuation/${id}/overview`, dot: () => 'none' },
  { key: 'company', label: () => 'Company', path: id => `/valuation/${id}/company`, dot: () => 'none' },
  { key: 'financials', label: () => 'Financials', path: id => `/valuation/${id}/financials`, dot: () => 'none' },
  { key: 'adjust', label: () => 'Review and adjust', path: id => `/valuation/${id}/adjust`, dot: () => 'none', dimUntilYearRead: true },
  { key: 'intake', label: () => 'Intake', path: id => `/valuation/${id}/intake`, dot: s => (s === 'draft' ? 'none' : 'done') },
  { key: 'methodology', label: () => 'Methodology', path: id => `/valuation/${id}/tuning`, dot: s => (s === 'draft' ? 'none' : 'done') },
  { key: 'provenance', label: () => 'Value provenance', path: id => `/valuation/${id}/output`, dot: s => (s === 'computed' || s === 'signed' ? 'done' : 'none') },
  {
    key: 'signoff',
    label: () => 'Finalize',
    path: id => `/valuation/${id}/signoff`,
    dot: s => (s === 'signed' ? 'done' : 'none'),
  },
]

function Dot({ state }: { state: 'done' | 'blocked' | 'none' }) {
  if (state === 'none') return null
  return (
    <span
      aria-hidden="true"
      className="ml-1 inline-block h-1.5 w-1.5 rounded-full align-middle"
      style={{ backgroundColor: state === 'done' ? 'var(--good)' : 'var(--destructive)' }}
    />
  )
}

// PLAT-ADMIN-HANDOFF-1: open the tab now (a popup blocker only allows it inside the click), then send it
// to the one-time hand-off address; if the hand-off is off or fails, to the plain platform address.
function openAdmin(e: MouseEvent, adminUrl: string) {
  e.preventDefault()
  const tab = window.open('', '_blank')
  if (!tab) { window.open(adminUrl, '_blank', 'noopener,noreferrer'); return }
  tab.opener = null
  startHandoff().then(u => { tab.location.href = u ?? adminUrl })
}

export function ValuationShell() {
  const { user, logout } = useAuth()
  const { valuationId } = useParams<{ valuationId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [confirmingAbandon, setConfirmingAbandon] = useState(false)

  const inEngagement = !!valuationId

  const [companyName, setCompanyName] = useState<string | null>(null)
  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  const [version, setVersion] = useState(1)
  // VAL-ADMIN-LINK-1: super_admin only; the address is a server setting, '' = no link.
  const [adminUrl, setAdminUrl] = useState('')
  const isSuperAdmin = user?.role === 'super_admin'
  useEffect(() => {
    if (!isSuperAdmin) { setAdminUrl(''); return }
    let live = true
    fetchPlatformAdminUrl().then(u => { if (live) setAdminUrl(u) }).catch(() => { if (live) setAdminUrl('') })
    return () => { live = false }
  }, [isSuperAdmin])
  const [delivered, setDelivered] = useState(false)
  // VAL-WELCOME-1: 404/403 on the engagement GET swaps the page body for house text.
  const [notFound, setNotFound] = useState(false)
  // null = not known yet (or the read failed): the item stays normal, never dimmed on a guess.
  const [yearCount, setYearCount] = useState<number | null>(null)

  const state = engagement?.status === 'signed_off' ? 'signed' : deriveState(engagement?.status)

  useEffect(() => {
    // Fix 4: reset both when the ENGAGEMENT changes, not the route within it
    // — a switch between two non-null valuationIds must not keep showing the
    // previous engagement's name while the new fetch is in flight. Keyed only
    // on valuationId so a same-engagement pathname change (below) never
    // blanks the header first.
    setCompanyName(null)
    setEngagement(null)
    setVersion(1)
    setYearCount(null)
  }, [valuationId])

  // Review and adjust is dimmed until a fiscal year has been read from an upload.
  // Refetched on pathname change, so it lights up after the upload is read.
  useEffect(() => {
    if (!valuationId) return
    let live = true
    fetchFinancials(valuationId)
      .then(f => { if (live) setYearCount(f.rows.length) })
      .catch(() => { /* unknown stays normal */ })
    return () => { live = false }
  }, [valuationId, location.pathname])

  // VAL-FINALIZE-STATE-2: a finalize signs a new version while the status stays "signed", so the
  // effect below would not re-run; the event bumps this to re-read the history without a reload.
  const [versionTick, setVersionTick] = useState(0)
  useEffect(() => {
    const bump = () => setVersionTick(t => t + 1)
    window.addEventListener('valuation:engagement-changed', bump)
    return () => window.removeEventListener('valuation:engagement-changed', bump)
  }, [])

  useEffect(() => {
    // VAL-VERSION-DIFF-1: the badge's version comes from the saved history
    // once the engagement is signed — a fetch error keeps the fallback of 1
    // (the badge degrades, it does not throw).
    if (!valuationId || state !== 'signed') return
    let live = true
    fetchSnapshotHistory(valuationId)
      .then(res => {
        if (!live || res.snapshots.length === 0) return
        setVersion(Math.max(...res.snapshots.map(s => s.version)))
      })
      .catch(() => { /* keep the fallback of 1 */ })
    return () => { live = false }
  }, [valuationId, state, versionTick])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    // VAL-ACTOR-JOURNEY-1-SIGNER slice C: refetch on pathname change too, not
    // just valuationId — so navigating within the same engagement (e.g. after
    // Run valuation lands on Methodology) picks up the status change and the
    // sidebar's nextStep() "Next" marker updates. No state clear here: this
    // effect only ever refreshes an already-shown engagement.
    getValuationEngagement(valuationId)
      .then(en => { if (live) { setCompanyName(en.subject_company_name); setEngagement(en); setNotFound(false) } })
      .catch((err: unknown) => {
        // header degrades to the id label — page body surfaces any other error
        const status = (err as { response?: { status?: number } })?.response?.status
        if (live && (status === 404 || status === 403)) setNotFound(true)
      })
    return () => { live = false }
  }, [valuationId, location.pathname])

  // VAL-WALK-SCREENS-1 W7: a screen that changes status (Finalize) announces it;
  // refetch once so the badge and the Next marker follow without a navigation.
  useEffect(() => {
    if (!valuationId) return
    let live = true
    const onChanged = (e: Event) => {
      const id = (e as CustomEvent<{ id?: string }>).detail?.id
      if (id && id !== valuationId) return
      getValuationEngagement(valuationId)
        .then(en => { if (live) { setCompanyName(en.subject_company_name); setEngagement(en) } })
        .catch(() => { /* next navigation refetches */ })
    }
    window.addEventListener('valuation:engagement-changed', onChanged)
    return () => { live = false; window.removeEventListener('valuation:engagement-changed', onChanged) }
  }, [valuationId])

  // VAL-FINALIZE-DONE-1: signed AND a document on file = done; NEXT points nowhere.
  useEffect(() => {
    if (!valuationId || state !== 'signed') { setDelivered(false); return }
    let live = true
    const check = () => {
      Promise.resolve()
        .then(() => fetchSnapshot(valuationId))
        .then(res => { if (live) setDelivered(!!res?.documents_on_file && res?.snapshot?.status === 'final') })
        .catch(() => { /* unknown keeps the marker */ })
    }
    check()
    const onChanged = (e: Event) => {
      const id = (e as CustomEvent<{ id?: string }>).detail?.id
      if (!id || id === valuationId) check()
    }
    window.addEventListener('valuation:engagement-changed', onChanged)
    return () => { live = false; window.removeEventListener('valuation:engagement-changed', onChanged) }
  }, [valuationId, state, location.pathname])

  // VAL-ACTOR-JOURNEY-1-SIGNER R8: the id is a breadcrumb detail, not the
  // headline — the subject company name is what the user actually recognizes.
  // QA-15: while the name loads show the product's neutral loading text, never the id.
  const engagementNameLabel = companyName ?? 'Loading…'
  const heading = inEngagement ? engagementNameLabel : 'Business Valuation'

  // Fix 1: the "Next" marker comes from the same nextStep(engagement, role)
  // used everywhere else in this brief, not from tab position — a signer's
  // actual next screen is not always the tab one slot after the current one.
  const step = engagement ? nextStep(engagement, user?.role) : null
  // VAL-UX-SMALL-1: NEXT follows the step bar order, not only the engine answer.
  const nextRoute = engagement && valuationId && !delivered
    ? railNextRoute(step, valuationId, engagement.status, yearCount)
    : null

  const initials = useMemo(() => {
    // PLAT-USER-NAME-1: the auth layer falls back to the email when there is no
    // name — an email is no name, so it gets no initials (as firstNameOf).
    const name = user?.name ?? ''
    if (name.includes('@')) return ''
    return name.split(' ').filter(Boolean).map(part => part[0]).slice(0, 2).join('').toUpperCase()
  }, [user?.name])

  function handleAbandonRevision() {
    setConfirmingAbandon(false)
    if (valuationId) runCache.clear(valuationId)
    navigate(`/valuation/${valuationId}/output`)
  }

  return (
    <div className="valuation-shell flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--background)' }}>
      {/* Left rail */}
      <aside
        className="sticky top-0 flex h-screen w-[264px] flex-shrink-0 flex-col"
        style={{ backgroundColor: 'var(--sidebar)', color: 'var(--sidebar-foreground)' }}
      >
        {/* Product mark */}
        <div className="flex items-center gap-2 px-4 pb-4 pt-5">
          <span
            className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-md text-sm font-bold"
            style={{ backgroundColor: 'var(--accent)', color: 'var(--sidebar-primary-foreground)' }}
          >
            V
          </span>
          <span className="text-[15px] font-semibold">Business Valuation</span>
        </div>

        {/* Global links */}
        <div className="px-4 pb-4">
          <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-wide opacity-60">Valuation</div>
          <nav className="flex flex-col gap-0.5 text-sm">
            <Link to="/valuation" className="rounded px-2 py-1.5 hover:bg-white/5">Dashboard</Link>
            <Link to="/valuation/new" className="rounded px-2 py-1.5 hover:bg-white/5">New engagement</Link>
            {/* VAL-HISTORY-2: History is built — every engagement this login can see. */}
            <Link to="/valuation/history" className="rounded px-2 py-1.5 hover:bg-white/5">History</Link>
            {isSuperAdmin && adminUrl && (
              <a href={adminUrl} target="_blank" rel="noopener noreferrer"
                onClick={e => openAdmin(e, adminUrl)}
                className="rounded px-2 py-1.5 hover:bg-white/5">Admin</a>
            )}
          </nav>
        </div>

        {/* Open-engagement card */}
        {inEngagement && (
          <div className="mx-4 mb-4 rounded-lg bg-white/10 px-3 py-2.5">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide opacity-60">Open engagement</div>
            <div className="mt-1 truncate text-sm font-medium">{heading}</div>
          </div>
        )}

        {/* This-engagement tabs */}
        {inEngagement && (
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-wide opacity-60">This engagement</div>
            <nav className="flex flex-col gap-0.5 text-sm" aria-label="Engagement sections">
              {TABS.map(tab => {
                const dimmed = !!tab.dimUntilYearRead && yearCount === 0
                const path = tab.path(valuationId!)
                const active = location.pathname === path
                const isNext = !active && !!nextRoute && path === nextRoute
                const content = (
                  <span
                    className={`flex items-center justify-between rounded-r border-l-[3px] px-3 py-1.5 ${
                      active ? 'bg-white/10 font-semibold' : ''
                    } ${dimmed ? 'opacity-50' : active ? 'opacity-100' : 'opacity-80'}`}
                    style={{ borderLeftColor: active ? 'var(--accent)' : isNext ? 'var(--accent)' : 'transparent' }}
                  >
                    <span className="flex items-center gap-1.5">
                      {tab.label(state, version)}
                      {isNext && (
                        <span className="text-[9px] font-semibold uppercase tracking-wide opacity-70">Next</span>
                      )}
                    </span>
                    <Dot state={dimmed ? 'none' : tab.dot(state)} />
                  </span>
                )
                return <Link key={tab.key} to={path}>{content}</Link>
              })}
            </nav>
          </div>
        )}

        {/* User block — pinned to the bottom */}
        <div className="mt-auto border-t border-white/10 px-4 py-4">
          <div className="flex items-center gap-2">
            <span
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'var(--accent)', color: 'var(--sidebar-primary-foreground)' }}
            >
              {initials}
            </span>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-semibold">{user?.name}</div>
              <div className="truncate text-[11.5px] opacity-60">{user?.firm}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="mt-3 w-full rounded border border-white/20 px-3 py-1.5 text-left text-sm hover:bg-white/5"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* White page header */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3 md:px-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xs text-gray-500">
                <Link to="/valuation">Business Valuation</Link>
                {inEngagement && <> &rsaquo; <span style={{ color: 'var(--primary)' }}>{engagementNameLabel}</span></>}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-lg font-semibold" style={{ color: 'var(--foreground)' }}>
                {heading}
                {inEngagement && engagement !== null && (
                  <span
                    className="rounded px-2 py-0.5 text-xs font-medium"
                    style={
                      state === 'revising'
                        ? { color: 'var(--accent)', backgroundColor: 'var(--muted)' }
                        : { color: 'var(--muted-foreground)', backgroundColor: 'var(--muted)' }
                    }
                  >
                    {badgeLabel(state, version)}
                  </span>
                )}
              </div>
            </div>
            {inEngagement && state === 'revising' && (
              confirmingAbandon ? (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-600">
                    Discards the in-progress v{version + 1} changes; v{version} stays as signed. Continue?
                  </span>
                  <Button size="sm" variant="destructive" onClick={handleAbandonRevision}>Yes, abandon</Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmingAbandon(false)}>Cancel</Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setConfirmingAbandon(true)}>
                  &larr; Abandon revision
                </Button>
              )
            )}
          </div>
        </div>

        <main className="flex-1 overflow-auto p-4 md:p-6">
          {inEngagement && notFound ? <_ValuationNotFound /> : <Outlet />}
        </main>
      </div>
    </div>
  )
}
